import React, { useState } from 'react';
import { Lock, ArrowLeft, CheckCircle2, Star, Sparkles, Shield, Rocket, Trophy, Clock, BookOpen, Globe, Check } from 'lucide-react';
import IELTSUKVICourse from './IELTSUKVICourse';
import IELTSAcademicCourse from './IELTSAcademicCourse';
import PTEAcademicCourse from './PTEAcademicCourse';

interface EnglishPrepLabProps {
    onBack: () => void;
}

type CountryData = {
    name: string;
    flag: string;
    tests: string[];
    bgCode: string;
    iconUrl: string;
};

const COUNTRIES: CountryData[] = [
    {
        name: 'United Kingdom',
        flag: '🇬🇧',
        tests: ['IELTS for UKVI (Academic)', 'IELTS Academic', 'PTE Academic UKVI', 'PTE Academic', 'TOEFL iBT'],
        bgCode: 'UK',
        iconUrl: 'https://images.unsplash.com/photo-1546410531-bb4caa6b424d?q=80&w=2071&auto=format&fit=crop'
    },
    {
        name: 'Canada',
        flag: '🇨🇦',
        tests: ['IELTS Academic', 'TOEFL iBT', 'PTE Academic', 'Duolingo English Test'],
        bgCode: 'USA',
        iconUrl: 'https://images.unsplash.com/photo-1534005886616-24baafa1e033?w=800&q=80'
    },
    {
        name: 'Australia',
        flag: '🇦🇺',
        tests: ['IELTS Academic (including One Skill Retake)', 'PTE Academic', 'TOEFL iBT Australia', 'Cambridge C1 Advanced', 'LanguageCert Academic'],
        bgCode: 'UK',
        iconUrl: 'https://images.unsplash.com/photo-1528643542261-24fb2f00a5d2?w=800&q=80'
    },
    {
        name: 'Whole of Europe',
        flag: '🇪🇺',
        tests: ['IELTS Academic', 'TOEFL iBT', 'Cambridge (C1/C2)', 'PTE Academic', 'Duolingo (DET)'],
        bgCode: 'DEP',
        iconUrl: 'https://images.unsplash.com/photo-1473183570377-f203875dc9b3?w=800&q=80'
    },
    {
        name: 'Malaysia',
        flag: '🇲🇾',
        tests: ['IELTS Academic', 'TOEFL iBT', 'PTE Academic', 'Duolingo English Test'],
        bgCode: 'USA',
        iconUrl: 'https://images.unsplash.com/photo-1541336032412-2048a678540d?w=800&q=80'
    },
    {
        name: 'USA',
        flag: '🇺🇸',
        tests: ['TOEFL iBT', 'IELTS Academic', 'PTE Academic', 'Duolingo English Test'],
        bgCode: 'USA',
        iconUrl: 'https://images.unsplash.com/photo-1501504905252-473c47e087f8?w=800&q=80'
    }
];

const EnglishPrepLab: React.FC<EnglishPrepLabProps> = ({ onBack }) => {
    const [processStep, setProcessStep] = useState<'country' | 'test' | 'pricing' | 'course'>('country');
    const [selectedCountry, setSelectedCountry] = useState<CountryData | null>(null);
    const [selectedTest, setSelectedTest] = useState<string | null>(null);
    const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'quarterly'>('quarterly');

    const handleCountrySelect = (country: CountryData) => {
        setSelectedCountry(country);
        setProcessStep('test');
    };

    const handleTestSelect = (test: string) => {
        setSelectedTest(test);
        setProcessStep('pricing');
    };

    const handleBack = () => {
        if (processStep === 'course') setProcessStep('pricing');
        else if (processStep === 'pricing') setProcessStep('test');
        else if (processStep === 'test') setProcessStep('country');
        else onBack();
    };

    return (
        <div className="max-w-6xl mx-auto pb-20 animate-fadeIn fade-in">
            {processStep !== 'course' && (
                <button
                    onClick={handleBack}
                    className="flex items-center text-slate-500 hover:text-slate-800 mb-8 transition-all font-bold text-sm bg-white px-5 py-3 rounded-xl shadow-sm border border-slate-100 group w-fit"
                >
                    <ArrowLeft size={16} className="mr-2 group-hover:-translate-x-1 transition-transform" />
                    {processStep === 'country' ? 'Back to Dashboard' : 'Back'}
                </button>
            )}

            {processStep === 'country' && (
                <div className="bg-[#011E26] text-white p-10 md:p-14 rounded-[3rem] shadow-2xl relative overflow-hidden ring-1 ring-white/5">
                    {/* Atmospheric Glow */}
                    <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#009B8F]/10 rounded-full blur-[120px] -mr-48 -mt-48 pointer-events-none"></div>
                    <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-[#1DE9B6]/5 rounded-full blur-[120px] -ml-48 -mb-48 pointer-events-none"></div>
                    <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:40px_40px] pointer-events-none opacity-20"></div>

                    <div className="text-center relative z-10 mb-16">
                        <div className="inline-flex items-center gap-2 bg-[#009B8F]/20 text-[#1DE9B6] px-5 py-2 rounded-full font-bold text-xs tracking-[0.2em] uppercase mb-6 border border-[#009B8F]/30 backdrop-blur-md">
                            <Globe size={14} /> Diagnostic Engine
                        </div>
                        <h2 className="text-4xl md:text-5xl font-serif font-bold text-white mb-6 tracking-tight leading-tight">
                            Target <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#009B8F] to-[#1DE9B6]">Destination</span>
                        </h2>
                        <p className="text-slate-400 max-w-2xl mx-auto text-lg leading-relaxed">
                            Every global university requires specific linguistic standards. Choose your target destination to view approved pathways.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 relative z-10">
                        {COUNTRIES.map((c, i) => (
                            <div
                                key={i}
                                onClick={() => handleCountrySelect(c)}
                                className="group relative rounded-3xl overflow-hidden cursor-pointer shadow-lg hover:shadow-[#009B8F]/20 transition-all duration-500 border border-white/10 hover:border-[#1DE9B6]/50"
                            >
                                <div className="absolute inset-0 opacity-20 group-hover:opacity-40 transition-opacity duration-500">
                                    <div className="absolute inset-0 bg-gradient-to-t from-[#011E26] via-[#011E26]/50 to-transparent z-10"></div>
                                    <img src={c.iconUrl} className="w-full h-full object-cover" alt={c.name} />
                                </div>
                                <div className="relative z-20 p-8 flex flex-col items-center justify-center min-h-[260px] text-center bg-[#011E26]/40 backdrop-blur-sm group-hover:bg-[#011E26]/20 transition-colors h-full">
                                    <div className="text-7xl mb-6 transform group-hover:scale-125 group-hover:-translate-y-2 transition-all duration-500 drop-shadow-[0_0_30px_rgba(255,255,255,0.3)]">
                                        {c.flag}
                                    </div>
                                    <h3 className="text-2xl font-serif font-bold uppercase tracking-widest text-white mb-6 group-hover:text-[#1DE9B6] transition-colors">
                                        {c.name}
                                    </h3>
                                    <div className="mt-auto px-6 py-3 rounded-full font-bold text-xs tracking-widest uppercase border border-white/20 text-white group-hover:bg-gradient-to-r group-hover:from-[#009B8F] group-hover:to-[#1DE9B6] group-hover:text-[#011E26] group-hover:border-transparent transition-all duration-300">
                                        View {c.bgCode} Tests
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {processStep === 'test' && selectedCountry && (
                <div className="bg-[#011E26] text-white p-10 md:p-14 rounded-[3rem] shadow-2xl relative overflow-hidden ring-1 ring-white/5">
                    <div className="absolute inset-0 opacity-10 pointer-events-none">
                        <img src={selectedCountry.iconUrl} className="w-full h-full object-cover" alt="Background" />
                        <div className="absolute inset-0 bg-[#011E26]/80 backdrop-blur-xl"></div>
                    </div>

                    <div className="text-center relative z-10 mb-16">
                        <div className="flex justify-center items-center gap-4 mb-6">
                            <div className="w-20 h-20 rounded-full bg-white/5 backdrop-blur-xl border border-white/10 flex items-center justify-center text-5xl shadow-inner shadow-white/5">
                                {selectedCountry.flag}
                            </div>
                        </div>
                        <h2 className="text-4xl md:text-5xl font-serif font-bold text-white tracking-tight mb-4">
                            {selectedCountry.name} <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#009B8F] to-[#1DE9B6]">Tests</span>
                        </h2>
                        <p className="text-slate-400 max-w-2xl mx-auto text-lg">
                            Select the specific English proficiency test you plan to take for your application.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 relative z-10 max-w-4xl mx-auto">
                        {selectedCountry.tests.map((test, i) => (
                            <div
                                key={i}
                                onClick={() => handleTestSelect(test)}
                                className="bg-white/5 hover:bg-[#009B8F]/20 border border-white/10 hover:border-[#1DE9B6]/50 transition-all p-6 rounded-3xl cursor-pointer group flex items-center gap-6 backdrop-blur-md"
                            >
                                <div className="w-14 h-14 rounded-full bg-[#011E26] flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform shadow-lg border border-[#009B8F]/30">
                                    <BookOpen size={24} className="text-[#1DE9B6]" />
                                </div>
                                <div className="flex-1 text-left">
                                    <h3 className="text-lg font-bold text-white group-hover:text-[#1DE9B6] transition-colors leading-tight">
                                        {test}
                                    </h3>
                                </div>
                                <div className="w-8 h-8 rounded-full border border-white/10 flex items-center justify-center text-transparent group-hover:border-[#1DE9B6] group-hover:bg-[#1DE9B6]/10 group-hover:text-[#1DE9B6] transition-all">
                                    <Check size={16} />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {processStep === 'pricing' && (
                <div className="bg-white rounded-[3rem] shadow-2xl shadow-[#011E26]/5 border border-gray-100 overflow-hidden mt-4">
                    {/* Hero Section */}
                    <div className="bg-[#011E26] p-16 text-center text-white relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#009B8F]/10 rounded-full blur-[80px] -mr-48 -mt-48 pointer-events-none"></div>
                        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#1DE9B6]/10 rounded-full blur-[80px] -ml-32 -mb-32 pointer-events-none"></div>
                        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:40px_40px] pointer-events-none opacity-20"></div>

                        <div className="relative z-10 flex flex-col items-center">
                            <div className="inline-flex items-center gap-3 bg-white/5 backdrop-blur-md text-[#1DE9B6] px-5 py-2 rounded-full font-bold text-xs tracking-widest mb-10 border border-white/10 shadow-lg">
                                <span className="text-xl">{selectedCountry?.flag}</span>
                                <span className="uppercase text-white">{selectedCountry?.name}</span>
                                <span className="text-white/20">|</span>
                                <span className="text-[#1DE9B6]">{selectedTest}</span>
                            </div>
                            <div className="w-24 h-24 bg-gradient-to-br from-[#009B8F] to-[#1DE9B6] rounded-[2.5rem] flex items-center justify-center mb-10 shadow-[0_20px_40px_-10px_rgba(0,155,143,0.5)] transform -rotate-12 hover:rotate-0 transition-transform duration-500">
                                <Lock size={40} className="text-white" />
                            </div>
                            <h2 className="text-5xl font-serif font-bold mb-6 tracking-tight">Unlock Prep Hub</h2>
                            <p className="text-slate-400 max-w-xl mx-auto text-lg mb-8">Gain immediate access to AI diagnostics, personalized mock exams, and your complete study pipeline.</p>

                            <div className="flex flex-wrap justify-center gap-4 mt-2">
                                {[
                                    { icon: Sparkles, text: 'AI Diagnostics' },
                                    { icon: Rocket, text: 'Study Plan' },
                                    { icon: Trophy, text: 'Mock Exams' }
                                ].map((item, idx) => (
                                    <div key={idx} className="flex items-center gap-2 bg-white/5 border border-white/10 px-5 py-3 rounded-full backdrop-blur-md hover:bg-white/10 transition-colors shadow-sm">
                                        <item.icon size={16} className="text-[#1DE9B6]" />
                                        <span className="text-xs font-bold uppercase tracking-widest text-white">{item.text}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Pricing / Plan Selection */}
                    <div className="p-12 md:p-20">
                        <div className="text-center mb-16">
                            <h3 className="text-3xl font-serif font-bold text-[#011E26] mb-4">Choose Your Path to Success</h3>
                            <p className="text-slate-500 max-w-lg mx-auto text-base leading-relaxed">
                                Subscribe to gain complete access to our AI-driven preparation suite tailored for <b>{selectedTest}</b>.
                            </p>
                        </div>

                        <div className="grid md:grid-cols-2 gap-8 mb-16 max-w-5xl mx-auto">
                            {/* Monthly Plan */}
                            <div
                                onClick={() => setSelectedPlan('monthly')}
                                className={`
                                    relative p-10 rounded-[2.5rem] border-2 transition-all cursor-pointer group hover:-translate-y-1 block
                                    ${selectedPlan === 'monthly' ? 'border-[#009B8F] bg-[#009B8F]/5 shadow-xl shadow-[#009B8F]/10' : 'border-gray-100 bg-white hover:border-[#009B8F]/30 shadow-sm'}
                                `}
                            >
                                <div className="flex justify-between items-start mb-8">
                                    <div>
                                        <div className="flex items-center gap-2 text-yellow-500 mb-3">
                                            <Star size={18} fill="currentColor" />
                                            <span className="font-bold text-xs uppercase tracking-[0.2em]">Monthly Access</span>
                                        </div>
                                        <h4 className="text-4xl font-bold text-[#011E26]">£29 <span className="text-base font-normal text-slate-400">/ 1 Month</span></h4>
                                    </div>
                                    <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center transition-colors ${selectedPlan === 'monthly' ? 'border-[#009B8F] bg-[#009B8F] text-white' : 'border-gray-200 text-transparent'}`}>
                                        <CheckCircle2 size={20} />
                                    </div>
                                </div>

                                <ul className="space-y-5 mb-8">
                                    {[
                                        'AI English Diagnostics',
                                        'AI Voice Coach sessions',
                                        'Automated Study Paths',
                                        'Progress Dashboard',
                                        'Unlimited Practice Modules'
                                    ].map((benefit, idx) => (
                                        <li key={idx} className="flex items-center gap-4 text-slate-600 font-medium text-sm">
                                            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#009B8F]/10 flex items-center justify-center">
                                                <CheckCircle2 size={14} className="text-[#009B8F]" />
                                            </div>
                                            {benefit}
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* Quarterly Plan */}
                            <div
                                onClick={() => setSelectedPlan('quarterly')}
                                className={`
                                    relative p-10 rounded-[2.5rem] border-2 transition-all cursor-pointer group hover:-translate-y-1 block
                                    ${selectedPlan === 'quarterly' ? 'border-[#011E26] bg-[#011E26] text-white shadow-2xl shadow-[#011E26]/30' : 'border-gray-100 bg-white hover:border-[#009B8F]/30 shadow-sm'}
                                `}
                            >
                                <div className="absolute -top-5 right-10 bg-gradient-to-r from-[#009B8F] to-[#1DE9B6] text-[#011E26] text-[10px] font-bold px-5 py-2 rounded-full uppercase tracking-widest shadow-lg transform group-hover:scale-105 transition-transform z-10">
                                    Best Value
                                </div>

                                <div className="flex justify-between items-start mb-8">
                                    <div>
                                        <div className="flex items-center gap-2 text-[#1DE9B6] mb-3">
                                            <Star size={18} fill="currentColor" />
                                            <span className="font-bold text-xs uppercase tracking-[0.2em]">3-Month Plan</span>
                                        </div>
                                        <h4 className="text-4xl font-bold">£65 <span className={`text-base font-normal ${selectedPlan === 'quarterly' ? 'text-white/40' : 'text-slate-400'}`}>/ 3 Months</span></h4>
                                        <p className="text-[10px] font-bold text-[#011E26] mt-2 bg-[#1DE9B6] inline-block px-3 py-1 rounded-full uppercase tracking-widest">SAVE £22</p>
                                    </div>
                                    <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center transition-colors ${selectedPlan === 'quarterly' ? 'border-[#1DE9B6] bg-[#1DE9B6] text-[#011E26]' : 'border-gray-200 text-transparent'}`}>
                                        <CheckCircle2 size={20} />
                                    </div>
                                </div>

                                <div className={`mb-6 p-6 rounded-2xl ${selectedPlan === 'quarterly' ? 'bg-white/5 border border-white/10' : 'bg-slate-50 border border-slate-100'}`}>
                                    <p className="text-[10px] font-bold uppercase tracking-[0.2em] mb-4 opacity-70">Everything in monthly, plus:</p>
                                    <ul className="space-y-4">
                                        {[
                                            'Long-term progress tracking',
                                            'Adaptive study recommendations',
                                            'Better exam-readiness timeline'
                                        ].map((benefit, idx) => (
                                            <li key={idx} className="flex items-center gap-4 text-sm font-medium">
                                                <div className="flex-shrink-0 w-5 h-5 rounded-full bg-[#1DE9B6]/20 flex items-center justify-center">
                                                    <Check size={12} className="text-[#1DE9B6]" />
                                                </div>
                                                {benefit}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <div className="text-center">
                            <button
                                onClick={() => setProcessStep('course')}
                                className="bg-gradient-to-r from-[#009B8F] to-[#1DE9B6] text-[#011E26] px-14 py-5 rounded-full font-bold text-lg hover:scale-105 transition-all shadow-[0_10px_30px_-10px_rgba(0,155,143,0.6)] active:scale-95 mb-8"
                            >
                                Unlock Now for {selectedPlan === 'monthly' ? '£29' : '£65'}
                            </button>
                            <div className="flex flex-wrap items-center justify-center gap-6 text-slate-400">
                                <div className="flex items-center gap-2">
                                    <Shield size={16} className="text-[#009B8F]" />
                                    <span className="text-[10px] font-bold uppercase tracking-widest">Secure Payment</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Clock size={16} className="text-[#009B8F]" />
                                    <span className="text-[10px] font-bold uppercase tracking-widest">Instant Access</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Footer disclaimer */}
                    <div className="bg-slate-50 p-8 text-center border-t border-gray-200">
                        <p className="text-slate-500 text-xs tracking-wider">
                            Designed for <b>{selectedTest}</b> preparation to ensure meaningful, measurable progress globally.
                        </p>
                    </div>
                </div>
            )}

            {processStep === 'course' && selectedCountry && selectedTest && (
                selectedTest.includes('PTE Academic') ? (
                    <PTEAcademicCourse
                        onBack={() => setProcessStep('pricing')}
                        selectedTest={selectedTest}
                        selectedCountry={{ name: selectedCountry.name, flag: selectedCountry.flag }}
                    />
                ) : (selectedTest.includes('IELTS Academic') && !selectedTest.includes('UKVI')) ? (
                    <IELTSAcademicCourse
                        onBack={() => setProcessStep('pricing')}
                        selectedTest={selectedTest}
                        selectedCountry={{ name: selectedCountry.name, flag: selectedCountry.flag }}
                    />
                ) : (
                    <IELTSUKVICourse
                        onBack={() => setProcessStep('pricing')}
                        selectedTest={selectedTest}
                        selectedCountry={{ name: selectedCountry.name, flag: selectedCountry.flag }}
                    />
                )
            )}
        </div>
    );
};

export default EnglishPrepLab;
